/******************************************************************
 * 파일명	: frontend/src/helpers/personNameI18n.js
 * 설명		: 사람 이름의 언어별 표시
 *          한국어(또는 언어 미설정)는 호출부가 넘긴 현재 표시값을 그대로 돌려준다.
 *          베트남어·중국어는 `nameVie`·`nameChn`을 쓰고, 없으면 넘긴 값(한국어)을 쓴다.
 *          그 밖의 언어(영어 등)는 영문 이름을 쓴다.
 *            - 성·이름이 둘 다 있으면 `nameEngFirst nameEngLast`("이름 성") 순서
 *              (직원 등록 화면 기준 nameEngFirst = 영문 이름, nameEngLast = 영문 성. 예: GilDong / Go → "GilDong Go")
 *            - 한 칸만 있으면 그 값
 *            - 둘 다 없으면 넘긴 값
 ******************************************************************/

import i18n from 'i18next';

export const isKoreanLanguage = (language) => !language || String(language).startsWith('ko');

const baseLanguage = (language) => String(language || 'ko').split('-')[0].toLowerCase();

const trimText = (value) => (typeof value === 'string' ? value.trim() : '');

/** 언어별 이름 칸. 영어는 성·이름 두 칸이라 따로 처리한다 */
const NAME_FIELD_BY_LANGUAGE = { vi: 'nameVie', ch: 'nameChn' };

/** 사람 객체의 다국어 이름 필드 */
const NAME_FIELDS = ['nameEngFirst', 'nameEngLast', 'nameVie', 'nameChn'];

const getEnglishName = (person) => {
	const engFirst = trimText(person?.nameEngFirst);
	const engLast = trimText(person?.nameEngLast);
	return [engFirst, engLast].filter(Boolean).join(' ');
};

/**
 * @param {object} person      nameEngFirst·nameEngLast·nameVie·nameChn을 가진 사용자 객체(없어도 됨)
 * @param {string} language    현재 언어(i18n.language). 생략하면 i18next의 현재 언어
 * @param {string} koreanName  호출부의 현재 표시값(한국어·대체용). 생략하면 person.name
 */
export const getPersonDisplayName = (person, language = i18n.language, koreanName = person?.name) => {
	if (isKoreanLanguage(language)) return koreanName;

	const field = NAME_FIELD_BY_LANGUAGE[baseLanguage(language)];
	const localized = field ? trimText(person?.[field]) : getEnglishName(person);
	return localized || koreanName;
};

/** 결재선 번역 객체 접미사. 한국어는 원본, 베트남어·중국어 외에는 영어 */
const approvalLangSuffix = (language) => {
	if (isKoreanLanguage(language)) return null;
	return { vi: 'Vie', ch: 'Chn' }[baseLanguage(language)] || 'Eng';
};

/**
 * 결재선 사용자의 표시값(name·rank·position·department).
 * 결재선 JSON은 저장할 때 사용자 옆에 번역본을 함께 담는다.
 *   - 결재선 항목: { user, userEng, userVie, userChn, otherUser, otherUserEng, … }
 *   - 참조자:      { name, …, userEng, userVie, userChn }
 * 번역본의 값이 없으면(번역 없음·번역본 저장 전 데이터) options.fallback, 없으면 원본을 쓴다.
 * 직위·직책(rank·position) 원본은 외국어 화면에서 번역 JSON(t)을 한 번 거친다(예: '대리' → 'Assistant Manager').
 * 이름은 원본에 nameEngFirst 등이 있으면 getPersonDisplayName 규칙을 한 번 더 적용한다.
 * @param {object} holder  결재선 항목 또는 참조자(또는 withApprovalLineLangs로 번역본을 붙인 사용자)
 * @param {object} options { key: 'user' | 'otherUser', language, fallback }
 */
export const getApprovalUserValue = (holder, field = 'name', options = {}) => {
	const { key = 'user', language = i18n.language } = options;
	if (!holder) return 'fallback' in options ? options.fallback : undefined;
	const nested = holder[key];
	const base = nested && typeof nested === 'object' ? nested : holder;
	const suffix = approvalLangSuffix(language);
	const localized = suffix ? trimText(holder[`${key}${suffix}`]?.[field]) : '';
	if (localized) return localized;
	if ('fallback' in options) return options.fallback;
	if (field === 'name') return getPersonDisplayName(base, language, base?.name);
	const value = base?.[field];
	if ((field === 'rank' || field === 'position') && trimText(value) && !isKoreanLanguage(language)) return i18n.t(value) || value;
	return value;
};

/**
 * 결재선 항목에서 사용자만 꺼내 쓰는 화면용. 항목의 번역본(userEng 등)을 사용자 사본에 붙인다(원본은 바꾸지 않음).
 * 예) line.map((l) => l.user) → line.map((l) => withApprovalLineLangs(l))
 */
export const withApprovalLineLangs = (item, key = 'user') => {
	const user = item?.[key];
	if (!user || typeof user !== 'object') return user;
	const langs = ['Eng', 'Vie', 'Chn'].filter((lang) => item[`${key}${lang}`] !== undefined);
	if (!langs.length) return user;
	return { ...user, ...Object.fromEntries(langs.map((lang) => [`user${lang}`, item[`${key}${lang}`]])) };
};

/**
 * 이름이 평면 필드로 오는 응답에서 사람 이름 칸만 꺼낸다.
 * 예) 결재 목록 { user: '고길동', userNameEngFirst, userNameEngLast, userNameVie, userNameChn } → pickPersonNames(row, 'user')
 *     마감 { cnfrmWorkerName, cnfrmWorkerNameEngFirst, … } → pickPersonNames(attend, 'cnfrmWorker')
 */
export const pickPersonNames = (obj, prefix) => {
	if (!obj) return undefined;
	return Object.fromEntries(NAME_FIELDS.map((key) => [key, obj[`${prefix}N${key.slice(1)}`]]));
};

/**
 * 결재 상세 응답(/request/detail)의 결재자·참조자 다국어 이름을 userId별로 모은다.
 * 결재 상세 화면은 결재선·참조자를 목록 데이터로 그리므로, 이 대응표로 다국어 이름만 보충한다.
 */
export const collectEnglishNamesByUserId = (detail) => {
	const result = {};
	const put = (person) => {
		const userId = person?.userId;
		if (userId === undefined || userId === null || userId === '') return;
		if (!NAME_FIELDS.some((key) => key in person)) return;
		result[String(userId)] = Object.fromEntries(NAME_FIELDS.map((key) => [key, person[key] ?? null]));
	};
	(Array.isArray(detail?.approvals) ? detail.approvals : []).forEach((approval) => {
		(Array.isArray(approval?.line) ? approval.line : []).forEach((line) => put(line?.user));
	});
	(Array.isArray(detail?.ccUsers) ? detail.ccUsers : []).forEach(put);
	return result;
};

/** 사람 객체에 다국어 이름이 없으면 대응표의 값을 보충한 사본을 돌려준다(원본은 바꾸지 않음) */
export const withEnglishName = (person, englishNamesByUserId) => {
	if (!person || !englishNamesByUserId) return person;
	if (NAME_FIELDS.some((key) => key in person)) return person;
	const found = englishNamesByUserId[String(person.userId)];
	return found ? { ...person, ...found } : person;
};
